import {Button, Col, DatePicker, InputNumber, Row} from 'antd';
import FloatLabel from "../FloatLabel";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {addPeriod} from "../../store/actions/RentalPeriodAction";
import {decimalToCentsNoRound, formatCurrency} from "../../utils/monetary";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const initialState = {
  lot_id:null,
  start_at:null,
  end_at:null,
  amount:null,
}

export default function FormPeriod({lot_id}) {
  const [isEditing, setIsEditing] = useState(false);
  const [period, setPeriod] = useState({...initialState, lot_id: lot_id});
  const [errors, setErrors] = useState({});

  const { periods, loading } = useSelector(state => state.rentalPeriods);
  const dispatch = useDispatch();

  useEffect(() => {
    // Le lot change, on reinitialize la période
    setPeriod({...initialState, lot_id: lot_id});
  }, [lot_id]);

  const validatePeriod = (value, field) => {
    const date = dayjs(value);

    return periods.some(p => {
      const start = dayjs(p.start_at, 'DD/MM/YYYY');
      const end = dayjs(p.end_at, 'DD/MM/YYYY');

      if (field === 'start_at') {
        // Pour une date de début, elle peut être égale à une date de fin existante
        // mais elle ne doit pas être strictement comprise entre start et end (exclu end)
        return date.isBetween(start, end, 'day', '[)');
      } else if (field === 'end_at') {
        // Pour une date de fin, elle peut être égale à une date de début existante
        // mais elle ne doit pas être strictement comprise entre start et end (exclu start)
        return date.isBetween(start, end, 'day', '(]');
      }
      return date.isBetween(start, end, 'day', '[]');
    });
  }

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'start_at':
        if (!value) {
          newErrors.start_at = "La date de début est obligatoire";
        } else if (validatePeriod(value, 'start_at')) { // Ajout du paramètre 'start_at'
          newErrors.start_at = "La date de début est dans une période";
        } else {
          delete newErrors.start_at;
        }
        break;
      case 'end_at':
        if (!value) {
          newErrors.end_at = "La date de fin est obligatoire";
        } else if (validatePeriod(value, 'end_at')) { // Ajout du paramètre 'end_at'
          newErrors.end_at = "La date de fin est dans une période";
        } else if (period.start_at && value.isBefore(period.start_at, 'day')) { // Ajout de 'day' pour comparer uniquement la date
          newErrors.end_at = "La date de fin doit être égale ou après la date de début";
        } else {
          delete newErrors.end_at;
        }
        break;
      case 'amount':
        if (!value || value <= 0) {
          newErrors.amount = "Le montant doit être supérieur à 0";
        } else {
          delete newErrors.amount;
        }
        break;
      default:
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const validateAll = () => {
    const newErrors = {};

    if (!period.start_at) {
      newErrors.start_at = "La date de début est obligatoire";
    } else if (validatePeriod(period.start_at, 'start_at')) { // Ajout du paramètre 'start_at'
      newErrors.start_at = "La date de début est dans une période";
    }

    if (!period.end_at) {
      newErrors.end_at = "La date de fin est obligatoire";
    } else if (validatePeriod(period.end_at, 'end_at')) { // Ajout du paramètre 'end_at'
      newErrors.end_at = "La date de fin est dans une période";
    } else if (period.start_at && period.end_at.isBefore(period.start_at, 'day')) {
      newErrors.end_at = "La date de fin doit être égale ou après la date de début";
    }

    if (!period.amount || period.amount <= 0) {
      newErrors.amount = "Le montant doit être supérieur à 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const onDateChange = (field) => (date, datestring) => {
    if (field === 'start_at') {
      setPeriod({...period, start_at: date, end_at: date})
      validateField('start_at', date);
      if (period.end_at) {
        validateField('end_at', period.end_at);
      }
    } else {
      setPeriod({...period, [field]: date})
      validateField(field, date);
    }
    //console.log(date);
  }

  const onAmountChange = value => {
    setPeriod({...period, amount: value})
    validateField('amount', value);
    //console.log(value);
  }

  const onFinish = () => {
    if (validateAll()) {
      //console.log(period);
      dispatch(addPeriod(period));
      // Réinitialiser le formulaire et les erreurs après succès
      setPeriod({...initialState, lot_id: lot_id});
      setErrors({});
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onFinish();
    }
  }

  return (
    <>
      <Row gutter={10} style={{width:'100%', margin: '20px 0'}}>
        {/*Input start_at*/}
        <Col sm={24} lg={12} xl={6}>
          <FloatLabel label="Date début" value={period.start_at} >
            <DatePicker
              value={period.start_at}
              onChange={onDateChange('start_at')}
              onKeyDown={handleKeyDown}
              format="DD/MM/YYYY"
              placeholder=""
            />
            {errors.start_at && (
              <div style={{color: '#ff4d4f', fontSize: '12px',  marginTop: 0}}>
                {errors.start_at}
              </div>
            )}
          </FloatLabel>
        </Col>

        {/*Input end_at*/}
        <Col sm={24} lg={12} xl={6}>
          <FloatLabel label="Date fin" value={period.end_at} >
            <DatePicker
              value={period.end_at}
              onChange={onDateChange('end_at')}
              onKeyDown={handleKeyDown}
              format="DD/MM/YYYY"
              placeholder=""
            />
            {errors.end_at && (
              <div style={{color: '#ff4d4f', fontSize: '12px', marginTop: 0}}>
                {errors.end_at}
              </div>
            )}
          </FloatLabel>
        </Col>

        {/*Input amount*/}
        <Col sm={24} lg={12} xl={6}>
          <FloatLabel label="Montant" value={period.amount} >
            <InputNumber
              style={{width:'100%'}}
              value={period.amount}
              onChange={(newValue) => onAmountChange(newValue)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => {
                setIsEditing(true);
                // Sélectionner le texte de manière asynchrone
                setTimeout(() => {
                  e.target.select();
                }, 0);
              }}
              formatter={(value) => {
                if (isEditing){
                return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                }
                return value > 0 ? formatCurrency(decimalToCentsNoRound(value)) : '';
              }}
              parser={(value) => {
                return value.replace(/\s/g, '').replace(',', '.');
              }}
              onBlur={() => setIsEditing(false)}
              precision={2}
              min={0}
              step={0.01}
              controls={false}
              decimalSeparator="."
            />
            {errors.amount && (
              <div style={{color: '#ff4d4f', fontSize: '12px', marginTop: 0}}>
                {errors.amount}
              </div>
            )}
          </FloatLabel>
        </Col>

        {/*Bouton validation*/}
        <Col sm={24} lg={12} xl={6} style={{textAlign: 'left'}}>
          <Button
            disabled={loading}
            style={{padding: '22px 25px'}}
            type="primary"
            htmlType="button"
            onClick={onFinish}
          >
            Valider période
          </Button>
        </Col>
      </Row>

    </>
  );
}